import { Trans } from '@lingui/macro';
import { Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Favorite } from '../../models/Favorite';
import { Message } from '../../models/Message';
import { Room } from '../../models/Room';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import MessageItem from '../components/MessageItem';
import { UIMessage } from '../components/UIMessage';

type RoomFavoritesProps = {
    room: Room;
};

const RoomFavorites: React.FC<RoomFavoritesProps> = ({ room }) => {
    const [favs, setFavs] = useState<Favorite[]>([]);
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        if (!room?.id) return;

        setInProgress(true);
        Fire.msg.getFavorites(My.user.id, room.id).then((favorites) => {
            setFavs(favorites);
            setInProgress(false);
        }).catch((error) => {
            console.error('Failed to load favorites:', error);
            setInProgress(false);
        });
    }, [room?.id]);

    if (inProgress) {
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <div className="h-1 w-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary animate-progress" />
                </div>
                <div className="text-muted-foreground mt-4">
                    <Trans>Loading saved messages...</Trans>
                </div>
            </div>
        );
    }

    if (favs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-muted-foreground">
                <Star className="h-20 w-20 text-muted-foreground" />
                <div className="text-center max-w-md">
                    <div className="font-medium text-lg mb-2">
                        <Trans>No saved messages yet</Trans>
                    </div>
                    <div className="text-sm text-card-foreground">
                        <Trans>Star messages to save them for later</Trans>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-4 border-b bg-muted/50">
                <div className="flex items-center gap-2 text-sm text-card-foreground">
                    <Star className="h-4 w-4 fill-primary text-primary" />
                    <span>
                        <Trans>{favs.length} saved messages</Trans>
                    </span>
                </div>
            </div>

            <div className="flex flex-col divide-y">
                {favs.map((f) => (
                    <div key={f.id} className="p-4 bg-background">
                        <FavoriteItem fav={f} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const FavoriteItem: React.FC<{ fav: Favorite }> = ({ fav }) => {
    const [m] = useState<UIMessage>(new UIMessage(fav.msg, []));
    const [inProgress, setInProgress] = useState(false);

    return (
        <>
            <div className="message-group">
                <div className="message-box">
                    <MessageItem
                        m={m}
                        state='favorite'
                        onMessageClick={() => { }}
                        parent={m.msg.parentMessageId ? emptyUIMessage() : undefined}
                        isViewButton={true}
                    />
                </div>
                {m.nonReactions().map(c => (
                    <div key={c.msg.id}>
                        <MessageItem
                            m={c}
                            state='favorite'
                            onMessageClick={() => { }}
                            parent={m}
                        />
                    </div>
                ))}
                {inProgress && (
                    <div className="h-1 w-full bg-muted overflow-hidden mt-2">
                        <div className="h-full bg-primary animate-progress" />
                    </div>
                )}
            </div>
        </>
    );

    function emptyUIMessage() {
        return new UIMessage(new Message(new Room("", ""), My.user), []);
    }
};

export default RoomFavorites;
